<template>
  <div class="chat-overlay-container" :class="{ 'is-visible': isVisible }">
    <!-- Dark backdrop -->
    <div class="chat-overlay-backdrop" :class="{ 'is-visible': isVisible }" @click="closeOverlay" />

    <!-- Overlay content -->
    <div class="chat-overlay" :class="{ 'is-visible': isVisible }" :style="overlayStyle">
      <!-- Collapse arrow at top center -->
      <OverlayCollapse @click="closeOverlay" />

      <div v-if="interactionView === InteractionView.LIVE_CHAT_INDICATOR" class="live-chat-container">
        <LiveChatIndicator :disabled="isisLiveChatIndicatorDisabled" @end-chat="handleEndLiveChat" />
      </div>

      <div v-show="isMessagesVisible" ref="messageContainer" class="chat-overlay-body">
        <template v-for="item in feedItems">
          <!-- Live Voice AI transcript, anchored inline in the feed -->
          <VoiceTranscript
            v-if="item.kind === 'voice-transcript'"
            :key="item.key"
            :messages="voiceTranscriptMessages"
          />

          <div v-else :key="`message-${item.key}`">
            <ChatItem
              :item="item.message"
              :previous="item.previous"
              :next="item.next"
              @scroll-bottom="scrollToBottom"
              @message:input="submitFlowAction"
              @external-event="submitFlowExternalAction"
            />
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useWidgetConversation } from '@/stores/useWidgetConversation';
import { storeToRefs } from 'pinia';
import { ref, computed, watch, nextTick, onMounted } from 'vue';
import ChatItem from '@/components/chat/ChatItem.vue';
import OverlayCollapse from './OverlayCollapse.vue';
import LiveChatIndicator from '@/components/LiveChatIndicator/LiveChatIndicator.vue';
import VoiceTranscript from '@/components/VoiceCallIndicator/VoiceTranscript.vue';
import { useOverlayCustomisation } from '../composables/useOverlayCustomisation';
import { useChatFeed } from '../composables/useChatFeed';
import { InteractionView, useChatWidget } from '@/stores/useChatWidget';
import { ExternalBotEvent, ResolutionType } from '@/common/bot/enum';
import { randomId } from '@/common/bot/lib';

// Props
interface Props {
  isVisible: boolean;
  interactionHeight: number;
}

const props = defineProps<Props>();

// Emits
const emit = defineEmits<{
  (e: 'close'): void;
}>();

// Customisation
const customisation = useOverlayCustomisation();

const isMessagesVisible = computed(() => {
  return interactionView.value !== InteractionView.CSAT && props.isVisible;
});

const overlayStyle = computed(() => {
  const color = customisation.backgroundColor.value;
  const height = isMessagesVisible.value ? `calc(85vh - ${props.interactionHeight}px)` : 'auto';

  return {
    ...(color ? { backgroundColor: color } : {}),
    height,
  };
});

// Refs
const isEndLiveChatDisabled = ref(false);
const messageContainer = ref<HTMLDivElement | null>(null);

const chatWidget = useChatWidget();
const { interactionView } = storeToRefs(chatWidget);

const { feedItems, voiceTranscriptMessages } = useChatFeed();

// Computed
const widgetConversation = useWidgetConversation();
const { aiFlow, supportWizard } = storeToRefs(widgetConversation);
const { startFlow, submitFlowAction, submitFlowExternalAction, onEndLiveChat } = widgetConversation;

const isAiChatInputDisabled = computed(() => {
  return aiFlow.value.active && aiFlow.value.userInputDisabled;
});

const isisLiveChatIndicatorDisabled = computed(() => {
  return Boolean(
    isAiChatInputDisabled.value || isEndLiveChatDisabled.value || supportWizard.value.support.supportLoading
  );
});

// Methods
const closeOverlay = () => {
  emit('close');
};

const scrollToBottom = () => {
  nextTick(() => {
    if (messageContainer.value) {
      messageContainer.value.scrollTop = messageContainer.value.scrollHeight;
    }
  });
};

const handleEndLiveChat = () => {
  isEndLiveChatDisabled.value = true;
  submitFlowExternalAction({ type: ExternalBotEvent.END_LIVE_CHAT, id: randomId() });
};

onEndLiveChat(({ resolution }) => {
  if (resolution === ResolutionType.NO) {
    isEndLiveChatDisabled.value = false;
  }
});

// Watchers - Combined watcher for all scroll triggers
watch(
  [feedItems, voiceTranscriptMessages, () => props.isVisible, interactionView, () => props.interactionHeight],
  () => {
    scrollToBottom();
  },
  { deep: true, immediate: true }
);

onMounted(() => {
  startFlow();
});
</script>

<style lang="scss" scoped>
.chat-overlay-container {
  opacity: 0;
  transform: translateY(100%);
  transition: opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1);

  &.is-visible {
    opacity: 1;
    transform: translateY(0);
  }
}

.chat-overlay-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 2;
  pointer-events: auto;

  width: 100vw;
  height: 100vh;

  opacity: 0;
  transition: opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1);

  &.is-visible {
    transform: translateY(-100vh);
    opacity: 1;
  }
}

.chat-overlay {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  border-radius: 20px 20px 0 0;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  z-index: 3;
  overflow: hidden;
  transform: translateY(100%);
  transition:
    transform 0.4s cubic-bezier(0.4, 0, 0.2, 1),
    max-height 0.3s ease;

  &.is-visible {
    transform: translateY(0);
  }
}

.chat-overlay-body {
  flex: 1;
  overflow-y: auto;
  padding: 12px 12px 0px 12px; // Added bottom padding to account for fixed input
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.live-chat-container {
  position: sticky;
  top: 0;
  z-index: 2;
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  padding: 0 12px;
}
</style>
